// src/features/gatherings/components/gathering-detail/PendingEditReviewPanel.tsx
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Import Grid directly as per MUI v7 pattern
import { format, parseISO } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Gathering, GatheringPendingEdit } from '../../../../types';
import { approveGatheringPendingEdit } from '../../../../services/api/endpoints/gatherings';
import {
  PENDING_EDIT_TONE_PALETTE,
  PendingEditFieldDiff,
  derivePendingEditDiff,
  describePendingEditStatus,
  formatPendingEditValue,
  resolveProposedImageUrl
} from '../../utils/pendingEdit';
import RejectPendingEditModal from '../modals/RejectPendingEditModal';

interface PendingEditReviewPanelProps {
  gathering: Gathering;
  pendingEdit: GatheringPendingEdit;
  token: string;
  /**
   * Called after a decision lands, with the gathering the API returned. The
   * caller is responsible for refreshing anything else that renders it.
   */
  onReviewed?: (gathering: Gathering) => void;
}

/**
 * Long text (a description or notes body) is clamped rather than given its own
 * scroll region: a nested scroller hides content with no affordance and fights
 * the page scroll. Past these limits the value collapses behind a Show more
 * toggle instead.
 */
const LONG_TEXT_CLAMP_LINES = 8;
const LONG_TEXT_CLAMP_CHARS = 400;

const formatTimestamp = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  try {
    return format(parseISO(value), 'PPpp');
  } catch (err) {
    console.error('Error formatting pending-edit timestamp:', err);
    return value;
  }
};

/** One side of a field diff: the label plus the value rendered for its type. */
const DiffValue: React.FC<{
  diff: PendingEditFieldDiff;
  side: 'current' | 'proposed';
  gathering: Gathering;
}> = ({ diff, side, gathering }) => {
  const value = side === 'current' ? diff.currentValue : diff.proposedValue;
  const isProposed = side === 'proposed';
  // Hooks must run on every render, so both live above the per-kind returns.
  const [imageFailed, setImageFailed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  if (diff.kind === 'image') {
    // The live side already has a resolved URL from the API; the proposed side
    // is only an object key, so its URL has to be composed (see
    // resolveProposedImageUrl). Composition can be wrong — a stale CDN
    // constant, an object that was never uploaded — hence onError.
    const imageUrl =
      side === 'current'
        ? gathering.image_url || null
        : resolveProposedImageUrl(gathering, value);
    const canPreview = Boolean(imageUrl) && !imageFailed;

    return (
      <Box>
        {imageUrl && !imageFailed && (
          <img
            src={imageUrl}
            alt={isProposed ? 'Proposed gathering image' : 'Current gathering image'}
            onError={() => setImageFailed(true)}
            style={{
              width: '100%',
              maxHeight: '200px',
              objectFit: 'cover',
              borderRadius: 8,
              display: 'block'
            }}
          />
        )}

        {/*
          An image that will not render is not a diff row an admin can review —
          approving it applies a picture nobody has seen. Say that outright
          rather than dropping an italic "No preview available" that reads like
          an ordinary empty value. The live side genuinely can be empty (the
          gathering simply has no image), so only the proposed side warns.
        */}
        {!canPreview && isProposed && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            This proposed image cannot be previewed here, so approving it is
            approving an image you have not seen. Ask the host to send it, or
            open the object key below against the assets CDN, before deciding.
          </Alert>
        )}

        {!canPreview && !isProposed && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {gathering.image_url ? 'Live image failed to load' : 'No image'}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ wordBreak: 'break-all', display: 'block', mt: 0.5 }}
        >
          {formatPendingEditValue(value, 'text')}
        </Typography>
      </Box>
    );
  }

  if (diff.kind === 'boolean') {
    const asText = formatPendingEditValue(value, 'boolean');
    return (
      <Chip
        label={asText}
        size="small"
        variant={value === true ? 'filled' : 'outlined'}
        color={value === true ? 'primary' : 'default'}
      />
    );
  }

  const isBlank = value === null || value === undefined || value === '';
  const text = formatPendingEditValue(value, diff.kind);
  const isClampable =
    diff.kind === 'longText' &&
    !isBlank &&
    (text.length > LONG_TEXT_CLAMP_CHARS ||
      text.split('\n').length > LONG_TEXT_CLAMP_LINES);
  const isClamped = isClampable && !isExpanded;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-line',
          fontStyle: isBlank ? 'italic' : 'normal',
          color: isBlank ? 'text.secondary' : 'text.primary',
          ...(isClamped
            ? {
                display: '-webkit-box',
                WebkitLineClamp: LONG_TEXT_CLAMP_LINES,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }
            : {})
        }}
      >
        {text}
      </Typography>
      {isClampable && (
        <Button
          size="small"
          variant="text"
          onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
          sx={{ mt: 0.5, px: 0, minWidth: 0 }}
        >
          {isClamped ? 'Show more' : 'Show less'}
        </Button>
      )}
    </Box>
  );
};

/**
 * Admin review surface for a host's staged edit to a live gathering
 * (ENG-856 backend, ENG-1097 UI).
 *
 * Staged edits leave the gathering PUBLISHED on purpose, so this panel is the
 * only place they surface in the dashboard. It shows the field-by-field diff
 * (live value → proposed value) and the approve / reject actions.
 */
const PendingEditReviewPanel: React.FC<PendingEditReviewPanelProps> = ({
  gathering,
  pendingEdit,
  token,
  onReviewed
}) => {
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  // Set once a decision lands. Normally the parent refreshes and this panel
  // unmounts, but the outcome must not depend on the parent having wired
  // `onReviewed` — without this, a caller that does not refresh would leave the
  // approve button live against an edit that is already gone.
  const [outcome, setOutcome] = useState<'approved' | 'rejected' | null>(null);

  const status = useMemo(
    () => describePendingEditStatus(pendingEdit.status),
    [pendingEdit.status]
  );
  const diff = useMemo(
    () => derivePendingEditDiff(gathering, pendingEdit),
    [gathering, pendingEdit]
  );

  const submittedAt = formatTimestamp(pendingEdit.updated_at || pendingEdit.created_at);
  const reviewedAt = formatTimestamp(pendingEdit.reviewed_at);

  const handleApprove = async () => {
    setIsApproving(true);
    setError(null);

    try {
      const updatedGathering = await approveGatheringPendingEdit(token, gathering.id);
      setOutcome('approved');
      if (onReviewed) {
        onReviewed(updatedGathering);
      }
    } catch (err) {
      // Approve is not guaranteed to succeed: the host may have withdrawn or
      // re-submitted the edit since this panel rendered, and once ENG-1096
      // lands the edit can also have expired or been superseded. Show what the
      // API actually said.
      console.error('Error approving pending edit:', err);
      setError(
        err instanceof Error && err.message
          ? `Could not apply these changes: ${err.message}`
          : 'Could not apply these changes. Please refresh and try again.'
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejected = (updatedGathering: Gathering) => {
    setOutcome('rejected');
    if (onReviewed) {
      onReviewed(updatedGathering);
    }
  };

  // Tinting the panel by tone matters: an amber wash on an expired or
  // unrecognised edit would read as "action needed" when no action is possible.
  const tone = PENDING_EDIT_TONE_PALETTE[status.tone];
  const canDecide = status.isActionable && outcome === null;
  // Approve applies `changes` to the gathering, so with nothing staged it would
  // be a no-op that still stamps the edit approved. Reject stays live on
  // purpose: it is the only way to clear a malformed or empty edit off the
  // board, which would otherwise keep its badge forever. Hence the asymmetry —
  // it is explained to the admin below rather than left to be discovered.
  const hasNothingStaged = diff.length === 0;
  const canApprove = canDecide && !hasNothingStaged;
  const approveBlockedReason = hasNothingStaged
    ? 'This edit stages no field changes, so there is nothing to apply. Reject it to clear it from the board.'
    : null;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        borderColor: tone.accent,
        borderLeftWidth: 4,
        bgcolor: tone.wash
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
        <EditNoteIcon sx={{ color: tone.accent }} />
        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
          {status.heading}
        </Typography>
        <Chip
          label={status.label}
          size="small"
          sx={{ bgcolor: tone.accent, color: 'white', fontWeight: 'bold' }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {status.description}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {submittedAt && (
          <Typography variant="caption" color="text.secondary">
            Submitted: {submittedAt}
          </Typography>
        )}
        {reviewedAt && (
          <Typography variant="caption" color="text.secondary">
            Reviewed: {reviewedAt}
          </Typography>
        )}
      </Box>

      {pendingEdit.review_notes && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Reviewer notes:</strong> {pendingEdit.review_notes}
        </Alert>
      )}

      {!status.isRecognised && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unrecognised review status <code>{status.raw || '(empty)'}</code>. Approve and
          reject are disabled until this dashboard understands it.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {outcome === 'approved' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          These changes were applied to the live gathering. The details below
          refresh to the new values.
        </Alert>
      )}

      {outcome === 'rejected' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          These changes were discarded. The live gathering is unchanged.
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {hasNothingStaged ? (
        <Typography variant="body2" color="text.secondary">
          This edit has no staged field changes.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {diff.map((entry) => (
            <Box key={entry.field}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {entry.label}
                </Typography>
                {!entry.isKnownField && (
                  <Tooltip
                    title="This field is not one the dashboard knows how to label. It is shown exactly as the API sent it."
                    arrow
                  >
                    <Chip label="Unrecognised field" size="small" variant="outlined" />
                  </Tooltip>
                )}
              </Box>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Current (live)
                  </Typography>
                  <DiffValue diff={entry} side="current" gathering={gathering} />
                </Grid>
                <Grid
                  size={{ xs: 12, md: 2 }}
                  sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                  {/*
                    The two values sit side by side from md up and stack
                    vertically below it, so the arrow has to follow the axis it
                    is describing — a right-pointing arrow between stacked
                    values points at nothing.
                  */}
                  <ArrowDownwardIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled', display: { xs: 'block', md: 'none' } }}
                  />
                  <ArrowForwardIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled', display: { xs: 'none', md: 'block' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Proposed
                  </Typography>
                  <DiffValue diff={entry} side="proposed" gathering={gathering} />
                </Grid>
              </Grid>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {/*
          A disabled MUI Button swallows pointer events, so the Tooltip needs a
          wrapper element to hang off — otherwise the reason approve is
          unavailable is invisible at exactly the moment it is needed.
        */}
        <Tooltip title={approveBlockedReason ?? ''} arrow disableHoverListener={!approveBlockedReason}>
          <span>
            <Button
              variant="contained"
              color="success"
              startIcon={
                isApproving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />
              }
              onClick={handleApprove}
              disabled={!canApprove || isApproving}
            >
              {isApproving ? 'Applying...' : 'Approve Changes'}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          color="error"
          startIcon={<BlockIcon />}
          onClick={() => setIsRejectModalOpen(true)}
          disabled={!canDecide || isApproving}
        >
          Reject Changes
        </Button>
        {canDecide && approveBlockedReason && (
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
            {approveBlockedReason}
          </Typography>
        )}
      </Box>

      <RejectPendingEditModal
        gatheringId={gathering.id}
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onSuccess={handleRejected}
        token={token}
        gatheringTitle={gathering.title}
      />
    </Paper>
  );
};

export default PendingEditReviewPanel;
